import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, onSnapshot, setDoc, doc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/hooks/useToast';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Radio, X } from 'lucide-react';
import { saveSessionOnEnd, storeSessionHistory } from '@/lib/sessionHistory';
import { buildKaraokeSessionHistoryExtras } from '@/lib/karaokeSessionAnalytics';
import { getKaraokeRequestsForSessionWindow } from '@/lib/sessionFunnelQueries';
import { isPaidSongRequestForLedger } from '@/lib/payoutLedger';
import { LiveSessionAnalytics, KaraokeSessionAnalytics } from '@/features/admin/components/AdminSessionAnalytics';
import { formatUkDateTime } from '@/lib/adminFormat';

export const LiveSessionsView = () => {
  const { format } = useCurrency();
  const { toast } = useToast();
  const [liveSessions, setLiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [artistMap, setArtistMap] = useState({});
  const [viewerCounts, setViewerCounts] = useState({});
  const [requestCounts, setRequestCounts] = useState({});
  const [earnings, setEarnings] = useState({});
  const [tipsByArtist, setTipsByArtist] = useState({});
  const [endingSessionDocId, setEndingSessionDocId] = useState(null);

  const persistForcedSessionHistory = async (session, artistId) => {
    const sessionId = session?.session_id;
    if (!sessionId) return;

    const startTime =
      session?.session_start_time?.toDate?.() ||
      session?.start_time?.toDate?.() ||
      (session?.session_start_time ? new Date(session.session_start_time) : new Date());
    const endTime = new Date();

    // Karaoke sessions use karaoke_requests, so we persist an explicit history payload.
    if (sessionId.startsWith('karaoke_')) {
      const karaokeRequests = await getKaraokeRequestsForSessionWindow(artistId, sessionId, startTime, endTime);
      const durationMinutes = Math.max(0, Math.round((endTime - startTime) / (1000 * 60)));
      const totalRequests = karaokeRequests.length;
      const playedRequests = karaokeRequests.filter((r) => r.status === 'performed').length;
      const totalEarnings = karaokeRequests
        .filter((r) => isPaidSongRequestForLedger(r))
        .reduce((sum, r) => sum + (Number(r.amount_paid) || Number(r.total_paid) || 0), 0);

      let funnelExtras = {};
      try {
        funnelExtras = await buildKaraokeSessionHistoryExtras(artistId, sessionId, startTime, endTime);
      } catch (funnelErr) {
        console.warn('Karaoke funnel (force end) failed:', funnelErr);
      }

      await storeSessionHistory({
        userId: artistId,
        sessionId,
        startTime,
        endTime,
        durationMinutes,
        totalRequests,
        playedRequests,
        totalEarnings,
        totalTips: 0,
        requests: karaokeRequests,
        tips: [],
        viewerCount: viewerCounts[artistId] || session.viewer_count || 0,
        peakViewers: viewerCounts[artistId] || session.viewer_count || 0,
        venue: session.venue || '',
        uniqueVisitors: funnelExtras.uniqueVisitors ?? 0,
        totalViews: funnelExtras.totalViews ?? 0,
        songsAddedToCart: funnelExtras.songsAddedToCart ?? 0,
        checkouts: funnelExtras.checkouts ?? 0,
        conversionRate: funnelExtras.conversionRate,
        karaoke_free_requests: funnelExtras.karaoke_free_requests,
        karaoke_paid_cart: funnelExtras.karaoke_paid_cart,
        karaoke_paid_checkouts: funnelExtras.karaoke_paid_checkouts,
        session_mode: 'karaoke',
      });
      return;
    }

    await saveSessionOnEnd(artistId, sessionId, startTime, endTime, session.venue || '');
  };

  const handleForceEndSession = async (session, artistId) => {
    const liveStatusDocId = session?.id || artistId;
    if (!liveStatusDocId) return;

    setEndingSessionDocId(liveStatusDocId);
    try {
      try {
        await persistForcedSessionHistory(session, artistId);
      } catch (historyError) {
        console.error('⚠️ Failed to save session history before force end:', historyError);
      }

      await setDoc(
        doc(db, 'live_status', liveStatusDocId),
        {
          is_live: false,
          session_end_time: Timestamp.now(),
          updated_at: Timestamp.now(),
          force_ended: true,
          force_ended_by: 'admin_console',
          force_ended_at: Timestamp.now(),
        },
        { merge: true }
      );

      toast({
        title: 'Session Ended',
        description: 'Live session was forced offline and archived.',
      });
    } catch (error) {
      console.error('❌ Error forcing session offline:', error);
      toast({
        title: 'Force End Failed',
        description: 'Could not end this session. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setEndingSessionDocId(null);
    }
  };

  useEffect(() => {
    if (!db) return;

    // Real-time listener for live_status collection
    const liveStatusQuery = query(
      collection(db, 'live_status'),
      where('is_live', '==', true)
    );

    const unsubscribe = onSnapshot(liveStatusQuery, async (snapshot) => {
      setLoading(true);
      try {
        const liveData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Get unique artist IDs
        const artistIds = [...new Set(liveData.map(s => s.user_id || s.id))];
        
        // Load artist profiles
        const artistProfiles = {};
        for (const artistId of artistIds) {
          try {
            const profileDoc = await getDoc(doc(db, 'profiles', artistId));
            if (profileDoc.exists()) {
              artistProfiles[artistId] = profileDoc.data();
            }
          } catch (err) {
            console.warn('Error loading artist profile:', err);
          }
        }
        setArtistMap(artistProfiles);

        // Fetch real-time data for each live session
        const viewerData = {};
        const requestData = {};
        const earningsData = {};
        const tipsData = {};

        for (const session of liveData) {
          const artistId = session.user_id || session.id;
          const sessionId = session.session_id;

          // Get active viewers count
          try {
            const activeViewersQuery = query(
              collection(db, 'active_viewers'),
              where('artist_id', '==', artistId)
            );
            const viewersSnapshot = await getDocs(activeViewersQuery);
            const now = Date.now();
            const fiveMinutesAgo = now - (5 * 60 * 1000);
            const activeViewers = viewersSnapshot.docs.filter(doc => {
              const data = doc.data();
              const lastSeen = data.last_seen?.toDate?.()?.getTime() || data.last_seen || 0;
              return lastSeen > fiveMinutesAgo;
            }).length;
            viewerData[artistId] = activeViewers;
          } catch (err) {
            console.warn('Error fetching viewers:', err);
            viewerData[artistId] = session.viewer_count || 0;
          }

          // Get requests for this session
          if (sessionId) {
            try {
              const requestsQuery = query(
                collection(db, 'requests'),
                where('artist_id', '==', artistId),
                where('session_id', '==', sessionId)
              );
              const requestsSnapshot = await getDocs(requestsQuery);
              const requests = requestsSnapshot.docs.map(doc => doc.data());
              requestData[artistId] = {
                total: requests.length,
                played: requests.filter(r => r.status === 'played' || r.played).length,
                pending: requests.filter(r => r.status === 'pending').length
              };
              
              // Calculate earnings
              const totalEarnings = requests.reduce((sum, r) => sum + (r.amount_paid || 0), 0);
              earningsData[artistId] = totalEarnings;

              const requestTipTotal = requests.reduce((sum, r) => sum + (Number(r.tip) || 0), 0);
              const requestTipCount = requests.filter((r) => (Number(r.tip) || 0) > 0).length;

              const tipsQuery = query(
                collection(db, 'tips'),
                where('artist_id', '==', artistId),
                where('session_id', '==', sessionId)
              );
              const tipsSnapshot = await getDocs(tipsQuery);
              const tipsDocs = tipsSnapshot.docs.map((doc) => doc.data());
              const tipsCollectionTotal = tipsDocs.reduce((sum, tip) => sum + (Number(tip.amount) || 0), 0);
              const tipsCollectionCount = tipsDocs.length;

              tipsData[artistId] = {
                total: Math.max(requestTipTotal, tipsCollectionTotal),
                count: Math.max(requestTipCount, tipsCollectionCount),
              };
            } catch (err) {
              console.warn('Error fetching requests:', err);
            }
          }
        }

        setViewerCounts(viewerData);
        setRequestCounts(requestData);
        setEarnings(earningsData);
        setTipsByArtist(tipsData);
        setLiveSessions(liveData);
      } catch (error) {
        console.error('Error loading live sessions:', error);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error in live sessions listener:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-2"></div>
        <p className="text-gray-400">Loading live sessions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Radio className="w-5 h-5 text-red-500 animate-pulse" />
            <span>Live Sessions ({liveSessions.length})</span>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Real-time view of all currently active live sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {liveSessions.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Radio className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No active live sessions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {liveSessions.map((session) => {
                const artistId = session.user_id || session.id;
                const artist = artistMap[artistId];
                const artistName = artist?.artist_name || artist?.karaoke_name || artist?.email || 'Unknown';
                const startTime = session.session_start_time?.toDate?.() || new Date(session.session_start_time);
                const endTime = session.session_end_time?.toDate?.() || null;
                const duration = startTime ? Math.round((new Date() - startTime) / (1000 * 60)) : 0;
                const activeViewers = viewerCounts[artistId] || session.viewer_count || 0;
                const requests = requestCounts[artistId] || { total: 0, played: 0, pending: 0 };
                const sessionEarnings = earnings[artistId] || 0;
                const sessionTips = tipsByArtist[artistId] || { total: 0, count: 0 };

                return (
                  <Card key={session.id} className="bg-gray-700 border-red-500/50">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>
                            <span className="font-bold text-white text-lg">{artistName}</span>
                            {session.venue && (
                              <Badge variant="outline" className="border-gray-500 text-gray-300">
                                {session.venue}
                              </Badge>
                            )}
                            {artist?.url_slug && (
                              <a
                                href={`https://yoursongrequest.com/${artist.url_slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-900/50 border border-blue-500/50 text-blue-300 hover:bg-blue-800/70 hover:text-blue-200 transition-colors"
                                title="Open artist's live public page"
                              >
                                <Radio className="w-3 h-3" />
                                /{artist.url_slug}
                              </a>
                            )}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm mt-4">
                            <div>
                              <p className="text-gray-400">Duration</p>
                              <p className="text-white font-semibold">{duration} min</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Active Viewers</p>
                              <p className="text-blue-400 font-semibold">{activeViewers}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Requests</p>
                              <p className="text-white font-semibold">{requests.total}</p>
                              <p className="text-gray-500 text-xs">{requests.played} played, {requests.pending} pending</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Earnings</p>
                              <p className="text-green-400 font-semibold">{format(sessionEarnings)}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Tips</p>
                              <p className="text-rose-300 font-semibold">{format(sessionTips.total || 0)}</p>
                              <p className="text-gray-500 text-xs">{sessionTips.count || 0} tip{(sessionTips.count || 0) === 1 ? '' : 's'}</p>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-600">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                              <div>
                                <p className="text-gray-500">Started</p>
                                <p className="text-gray-300">{formatUkDateTime(startTime)}</p>
                              </div>
                              {endTime && (
                                <div>
                                  <p className="text-gray-500">Scheduled End</p>
                                  <p className="text-gray-300">{formatUkDateTime(endTime)}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-gray-500">Session ID</p>
                                <p className="text-gray-300 font-mono text-xs">{session.session_id?.substring(0, 20)}...</p>
                              </div>
                            </div>
                            {session.session_id && (session.session_start_time || session.start_time) && (
                              <div className="mt-4">
                                <p className="text-xs text-gray-500 mb-2 font-medium">
                                  Live funnel (same numbers written to session history when the session ends)
                                </p>
                                {String(session.session_id).startsWith('karaoke_') ? (
                                  <KaraokeSessionAnalytics
                                    artistId={artistId}
                                    sessionId={session.session_id}
                                    sessionStartTime={session.session_start_time || session.start_time}
                                  />
                                ) : (
                                  <LiveSessionAnalytics
                                    artistId={artistId}
                                    sessionId={session.session_id}
                                    sessionStartTime={session.session_start_time || session.start_time}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={endingSessionDocId === session.id}
                          onClick={() => handleForceEndSession(session, artistId)}
                          className="ml-4 border-red-500/50 text-red-300 hover:bg-red-900/30 hover:text-red-200"
                          title="Force end this live session"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {endingSessionDocId === session.id ? 'Ending...' : 'End'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
